/**
 * Manifest Connection Service
 *
 * Extracts connection metadata from operation manifests.
 * Handles manifest-driven connection requirements including multi-slot support.
 */

import type { OperationManifest, ConnectionType, ConnectionReferenceKeyFormat } from '@/types/operationManifest';
import type {
  ManifestConnectionMetadata,
  ConnectionSlotDefinition,
} from '@/types/connectionValidation';
import { CONNECTION_PATTERNS } from '@/types/connectionValidation';
import { isManagedApiConnector } from '@/utils/connections';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Built-in operations that never require connections
 */
const NO_CONNECTION_OPERATIONS = new Set([
  'Compose',
  'Response',
  'ParseJson',
  'If',
  'Switch',
  'Foreach',
  'Scope',
  'Until',
  'Terminate',
  'Wait',
  'SetVariable',
  'InitializeVariable',
  'IncrementVariable',
  'DecrementVariable',
  'AppendToArrayVariable',
  'AppendToStringVariable',
  'Join',
  'Select',
  'Filter',
  'Create_CSV_table',
  'Create_HTML_table',
  'Delay',
  'Delay_until',
  'Recurrence',
  'Request', // Manual trigger
  'HTTP', // HTTP action doesn't require managed connection
  'RespondToChat', // Built-in chat response action
]);

/**
 * Default connection type mappings by operation type pattern
 */
const OPERATION_TYPE_TO_CONNECTION_TYPE: Record<string, ConnectionType> = {
  // Agent operations
  Agent: 'agent',
  AgentLoop: 'agent',
  // MCP operations
  Mcp: 'mcp',
  McpTool: 'mcp',
  // Azure Functions
  Function: 'function',
  // API Management
  ApiManagement: 'apimanagement',
  // Default for managed APIs
  ApiConnection: 'apiconnection',
  ApiConnectionWebhook: 'apiconnection',
  ApiConnectionNotification: 'apiconnection',
  // Service providers
  ServiceProvider: 'serviceprovider',
  ServiceProviderWebhook: 'serviceprovider',
};

/**
 * Default reference key format by connection type
 */
const CONNECTION_TYPE_TO_KEY_FORMAT: Record<ConnectionType, ConnectionReferenceKeyFormat> = {
  apiconnection: 'apiconnection',
  serviceprovider: 'serviceprovider',
  agent: 'agentconnection',
  mcp: 'mcpconnection',
  function: 'function',
  apimanagement: 'apimanagement',
};

// =============================================================================
// MAIN FUNCTION
// =============================================================================

/**
 * Extract connection metadata from an operation manifest
 *
 * @param manifest - Operation manifest (may be null)
 * @param operationType - Operation type (e.g., 'Http', 'Agent', 'ServiceBus')
 * @returns Connection metadata or null if no connection needed
 */
export function getConnectionMetadataFromManifest(
  manifest: OperationManifest | null | undefined,
  operationType: string
): ManifestConnectionMetadata | null {
  // Check if this operation type never needs a connection
  if (NO_CONNECTION_OPERATIONS.has(operationType)) {
    return null;
  }

  // If we have a manifest with connection metadata, use it
  if (manifest?.properties?.connection) {
    const conn = manifest.properties.connection;
    const refFormat = manifest.properties.connectionReference?.referenceKeyFormat;

    // Determine connection type
    const type = conn.type ?? inferConnectionType(operationType);

    return {
      required: conn.required,
      type,
      referenceKeyFormat: refFormat ?? inferReferenceKeyFormat(type),
      slots: buildConnectionSlots(operationType, type, conn.required),
      ...(conn.disableAutoSelection !== undefined && { disableAutoSelection: conn.disableAutoSelection }),
    };
  }

  // No manifest - infer from operation type
  return inferConnectionMetadata(operationType);
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Infer connection type from operation type
 */
export function inferConnectionType(operationType: string): ConnectionType {
  // Check direct mapping
  if (operationType in OPERATION_TYPE_TO_CONNECTION_TYPE) {
    return OPERATION_TYPE_TO_CONNECTION_TYPE[operationType] as ConnectionType;
  }

  // Check for patterns
  const lowerType = operationType.toLowerCase();

  if (lowerType.includes('agent')) return 'agent';
  if (lowerType.includes('mcp')) return 'mcp';
  if (lowerType.includes('function')) return 'function';
  if (lowerType.includes('apimanagement')) return 'apimanagement';
  if (lowerType.includes('serviceprovider')) return 'serviceprovider';

  // Default to API connection for managed APIs
  return 'apiconnection';
}

/**
 * Infer reference key format from connection type
 */
export function inferReferenceKeyFormat(connectionType: ConnectionType): ConnectionReferenceKeyFormat {
  return CONNECTION_TYPE_TO_KEY_FORMAT[connectionType] ?? 'apiconnection';
}

/**
 * Infer connection metadata for operations without a manifest
 */
export function inferConnectionMetadata(operationType: string): ManifestConnectionMetadata | null {
  // Operations that require connections even without a manifest
  const connectionOperations = [
    'ApiConnection',
    'ApiConnectionWebhook',
    'ApiConnectionNotification',
    'ServiceProvider',
    'Agent',
    'AgentLoop',
    'Mcp',
    'McpTool',
    'Function',
    'ApiManagement',
  ];

  // Check if this operation type typically requires a connection
  const requiresConnection = connectionOperations.some(
    (op) => operationType.includes(op) || operationType === op
  );

  if (!requiresConnection) {
    // Unknown operation type - assume no connection needed
    return null;
  }

  const type = inferConnectionType(operationType);

  return {
    required: true,
    type,
    referenceKeyFormat: inferReferenceKeyFormat(type),
    slots: buildConnectionSlots(operationType, type, true),
  };
}

/**
 * Build connection slot definitions based on operation type
 *
 * All operations have a single 'default' slot.
 * Agent operations use modelConfigurations.model1 path (matching LogicAppsUX pattern).
 */
export function buildConnectionSlots(
  operationType: string,
  connectionType: ConnectionType,
  required: boolean
): ConnectionSlotDefinition[] {
  // Agent operations use modelConfigurations path (single connection, not multi-slot)
  if (operationType === 'Agent' || operationType === 'AgentLoop' || connectionType === 'agent') {
    return [
      {
        slotId: 'default',
        displayName: 'AI Model Connection',
        required: true,
        allowedTypes: ['agent'],
        referencePath: ['inputs', 'modelConfigurations', 'model1'],
        description: 'Connection to AI model (Azure OpenAI, OpenAI, etc.)',
      },
    ];
  }

  // Standard single-slot connection
  const pattern = CONNECTION_PATTERNS[connectionType];
  return [
    {
      slotId: 'default',
      displayName: 'Connection',
      required,
      allowedTypes: [connectionType],
      referencePath: pattern?.referencePath ?? ['inputs', 'host', 'connection'],
    },
  ];
}

// =============================================================================
// SLOT UTILITIES
// =============================================================================

/**
 * Get the slot definition for a specific slot ID
 */
export function getSlotDefinition(
  metadata: ManifestConnectionMetadata | null,
  slotId: string
): ConnectionSlotDefinition | null {
  if (!metadata) return null;
  return metadata.slots.find((slot) => slot.slotId === slotId) ?? null;
}

/**
 * Get all required slots from connection metadata
 */
export function getRequiredSlots(
  metadata: ManifestConnectionMetadata | null
): ConnectionSlotDefinition[] {
  if (!metadata) return [];
  return metadata.slots.filter((slot) => slot.required);
}

/**
 * Get all optional slots from connection metadata
 */
export function getOptionalSlots(
  metadata: ManifestConnectionMetadata | null
): ConnectionSlotDefinition[] {
  if (!metadata) return [];
  return metadata.slots.filter((slot) => !slot.required);
}

/**
 * Check if an operation has multiple connection slots
 */
export function hasMultipleSlots(metadata: ManifestConnectionMetadata | null): boolean {
  return (metadata?.slots.length ?? 0) > 1;
}

/**
 * Check if metadata represents an Agent operation
 */
export function isAgentOperation(metadata: ManifestConnectionMetadata | null): boolean {
  return metadata?.type === 'agent';
}

// =============================================================================
// DISPLAY HELPERS
// =============================================================================

/**
 * Get display name for a connection type
 */
export function getConnectionTypeDisplayName(type: ConnectionType): string {
  const displayNames: Record<ConnectionType, string> = {
    apiconnection: 'API Connection',
    serviceprovider: 'Service Provider',
    agent: 'AI Model',
    mcp: 'MCP Connection',
    function: 'Azure Function',
    apimanagement: 'API Management',
  };
  return displayNames[type] ?? type;
}

/**
 * Get description for a connection type
 */
export function getConnectionTypeDescription(type: ConnectionType): string {
  const descriptions: Record<ConnectionType, string> = {
    apiconnection: 'Connect to external services via managed connectors',
    serviceprovider: 'Connect to Azure services like Service Bus, Event Hubs',
    agent: 'Connect to AI models like Azure OpenAI or OpenAI',
    mcp: 'Connect via Model Context Protocol',
    function: 'Connect to an Azure Function',
    apimanagement: 'Connect via Azure API Management',
  };
  return descriptions[type] ?? '';
}

// =============================================================================
// CONNECTION PARAMETER SET FUNCTIONS
// =============================================================================

import type {
  ConnectionParameter,
  ConnectionParameterSet,
  ConnectionParameterSets,
  FormValues,
} from '@/types/connectionParameters';
import { ParameterType as PT, type OAuthSettings, type ManagedIdentitySettings } from '@/types/connectionParameters';

/**
 * Connector manifest with connection parameters
 */
export interface ConnectorManifestWithParameters {
  /** Connector ID */
  id: string;
  /** Connector name */
  name: string;
  /** Connection parameters (legacy single-auth format) */
  connectionParameters?: Record<string, unknown>;
  /** Connection parameter sets (multi-auth format) */
  connectionParameterSets?: ConnectionParameterSets;
  /** Alternative connection parameters (e.g., managed identity for legacy OAuth connectors) */
  connectionAlternativeParameters?: Record<string, unknown>;
  /**
   * Pre-create test endpoint emitted by Otto runtime when the SP declares a
   * `TestConnectionOperationName` (see `ServiceProviderConstants.cs`). Form:
   * `/runtime/webhooks/workflow/api/management/OperationGroups/{group}/testConnection`.
   * When present, callers should validate the SP connection BEFORE writing it
   * to connections.json — matches LA Designer's `_testServiceProviderConnection`.
   */
  testConnectionUrl?: string;
}

/**
 * Minimal shape of a service-provider connector source for {@link buildConnectorManifest}.
 * Matches both the standalone `/operationGroups/{group}` response and the trimmed
 * `manifest.connector` copy embedded in `/operations/{op}?$expand=properties/manifest`.
 */
interface ConnectorSource {
  id?: string;
  name?: string;
  properties?: {
    connectionParameterSets?: { values?: unknown[] };
    testConnectionUrl?: string;
    [key: string]: unknown;
  };
}

function toManifest(source: ConnectorSource | undefined): ConnectorManifestWithParameters | null {
  const values = source?.properties?.connectionParameterSets?.values;
  if (!source?.id || !source.name || !values?.length) return null;
  const manifest: ConnectorManifestWithParameters = {
    id: source.id,
    name: source.name,
    connectionParameterSets: { values: values as ConnectionParameterSet[] },
  };
  if (source.properties?.testConnectionUrl) {
    manifest.testConnectionUrl = source.properties.testConnectionUrl;
  }
  return manifest;
}

/**
 * Build a {@link ConnectorManifestWithParameters} from the available connector
 * sources. Prefers the full standalone connector (from `/operationGroups/{group}`)
 * over the trimmed copy embedded in the operation manifest — the trimmed copy
 * drops auth-provider parameter sets (AAD: Authority/Tenant/CredentialType/Pfx/
 * Password, MSI: Identity).
 */
export function buildConnectorManifest(opts: {
  connectorId: string | null | undefined;
  serviceProviderConnector: ConnectorSource | null | undefined;
  operationManifest: { properties?: { connector?: ConnectorSource } } | null | undefined;
}): ConnectorManifestWithParameters | null {
  const { connectorId, serviceProviderConnector, operationManifest } = opts;
  if (!connectorId || isManagedApiConnector(connectorId)) return null;
  return (
    toManifest(serviceProviderConnector ?? undefined) ??
    toManifest(operationManifest?.properties?.connector)
  );
}

/**
 * Extract connection parameter sets from a connector manifest
 * Handles both legacy single-parameter format and multi-auth format.
 * This also filters out the parameters from each set which are marked for "gateway" capability, since those are not relevant for automation projects.
 */
export function getConnectionParameterSets(
  manifest: ConnectorManifestWithParameters | null | undefined
): ConnectionParameterSet[] {
  if (!manifest) return [];

  // Prefer multi-auth format if available (these are real API Hub sets)
  if (manifest.connectionParameterSets?.values?.length) {
    return manifest.connectionParameterSets.values.map(set => {
      const filteredParameters = Object.fromEntries(
        Object.entries(set.parameters).filter(([_, param]) => !(param.uiDefinition?.constraints?.capability?.includes('gateway') && param.uiDefinition?.constraints?.capability?.length === 1))
      );

      // Only normalize when the manifest actually provides a displayName.
      // If it doesn't (e.g. AzureBlob's ActiveDirectoryOAuth / ManagedServiceIdentity
      // sets ship no set-level uiDefinition), leave uiDefinition untouched so the
      // UI's `displayName ?? set.name` fallback — matching the Logic Apps Designer
      // behavior in libs/designer-v2/.../formInputs/connectionMultiAuth.tsx — kicks in.
      const rawDisplayName = set.uiDefinition?.displayName;
      if (!rawDisplayName) {
        return { ...set, parameters: filteredParameters };
      }
      const normalizedDisplayName = rawDisplayName.toLowerCase().includes('logic apps managed identity')
        ? 'Managed Identity'
        : rawDisplayName;

      return {
        ...set,
        parameters: filteredParameters,
        uiDefinition: {
          ...set.uiDefinition,
          displayName: normalizedDisplayName,
        },
      };
    });
  }

  // Fall back to legacy single-parameter format
  if (manifest.connectionParameters) {
    const filteredConnectionParameters = Object.fromEntries(
      Object.entries(manifest.connectionParameters).filter(([_, param]) => !((param as ConnectionParameter).uiDefinition?.constraints?.capability?.includes('gateway') && (param as ConnectionParameter).uiDefinition?.constraints?.capability?.length === 1))
    );
    return [convertLegacyParameters(filteredConnectionParameters, manifest.name)];
  }

  return [];
}

/**
 * Check whether the manifest has real multi-auth parameter sets from the API
 * (as opposed to a single synthetic set from convertLegacyParameters).
 * Use this to guard submit-path decisions like including parameterSetName.
 */
export function hasRealParameterSets(
  manifest: ConnectorManifestWithParameters | null | undefined
): boolean {
  return !!(manifest?.connectionParameterSets?.values?.length);
}

/**
 * Convert legacy connection parameters to ConnectionParameterSet format
 */
function convertLegacyParameters(
  params: Record<string, unknown>,
  connectorName: string
): ConnectionParameterSet {
  const parameters: ConnectionParameterSet['parameters'] = {};

  for (const [key, value] of Object.entries(params)) {
    const param = value as Record<string, unknown>;
    // Azure API Hub returns metadata nested under uiDefinition; some older
    // connectors also have top-level displayName/description/constraints.
    // Read nested first, fall back to top-level for compatibility.
    const nestedUiDef = param.uiDefinition as Record<string, unknown> | undefined;

    const uiDef: ConnectionParameterSet['parameters'][string]['uiDefinition'] = {
      displayName: (nestedUiDef?.displayName as string) ?? (param.displayName as string) ?? key,
    };
    const description = (nestedUiDef?.description ?? param.description) as string | undefined;
    if (description) {
      uiDef.description = description;
    }
    const tooltip = (nestedUiDef?.tooltip ?? param.tooltip) as string | undefined;
    if (tooltip) {
      uiDef.tooltip = tooltip;
    }
    const nestedConstraints = nestedUiDef?.constraints as Record<string, unknown> | undefined;
    const topConstraints = param.constraints as Record<string, unknown> | undefined;
    if (nestedConstraints || topConstraints) {
      uiDef.constraints = { ...topConstraints, ...nestedConstraints };
    }

    const paramDef: ConnectionParameterSet['parameters'][string] = {
      type: (param.type as string) ?? 'string',
      uiDefinition: uiDef,
    };
    if (param.visibility) {
      paramDef.visibility = param.visibility as string;
    }
    // Preserve oAuthSettings / managedIdentitySettings if present
    if (param.oAuthSettings) {
      paramDef.oAuthSettings = param.oAuthSettings as OAuthSettings;
    }
    if (param.managedIdentitySettings) {
      paramDef.managedIdentitySettings = param.managedIdentitySettings as ManagedIdentitySettings;
    }
    parameters[key] = paramDef;
  }

  return {
    name: 'default',
    uiDefinition: {
      displayName: connectorName,
      description: `Default authentication for ${connectorName}`,
    },
    parameters,
  };
}

/**
 * Get the default parameter set from a manifest
 */
export function getDefaultParameterSet(
  manifest: ConnectorManifestWithParameters | null | undefined
): ConnectionParameterSet | null {
  const sets = getConnectionParameterSets(manifest);
  return sets[0] ?? null;
}

/**
 * Find a parameter set by name
 */
export function findParameterSet(
  manifest: ConnectorManifestWithParameters | null | undefined,
  name: string
): ConnectionParameterSet | null {
  const sets = getConnectionParameterSets(manifest);
  return sets.find((s) => s.name === name) ?? null;
}

/**
 * Check if a connector requires OAuth authentication
 */
export function connectorRequiresOAuth(
  manifest: ConnectorManifestWithParameters | null | undefined,
  parameterSetName?: string
): boolean {
  const sets = getConnectionParameterSets(manifest);
  const targetSets = parameterSetName
    ? sets.filter((s) => s.name === parameterSetName)
    : sets;

  return targetSets.some((set) =>
    Object.values(set.parameters).some(
      (p) => p.type === PT.OAuthSetting || p.type === PT.OAuthConnection
    )
  );
}

/**
 * Check if a connector supports managed identity
 */
export function connectorSupportsManagedIdentity(
  manifest: ConnectorManifestWithParameters | null | undefined
): boolean {
  const sets = getConnectionParameterSets(manifest);
  return sets.some((set) =>
    Object.values(set.parameters).some((p) => p.type === PT.ManagedIdentity)
  );
}

/**
 * Get available auth types from a connector manifest
 */
export function getAvailableAuthTypes(
  manifest: ConnectorManifestWithParameters | null | undefined
): Array<{ name: string; displayName: string; description?: string }> {
  const sets = getConnectionParameterSets(manifest);
  return sets.map((set) => {
    const result: { name: string; displayName: string; description?: string } = {
      name: set.name,
      displayName: set.uiDefinition?.displayName ?? set.name,
    };
    if (set.uiDefinition?.description) {
      result.description = set.uiDefinition.description;
    }
    return result;
  });
}

/**
 * Set a value at a nested path inside an object, auto-vivifying intermediate
 * plain-object containers along the way. If a non-object already sits at an
 * intermediate key it's overwritten with `{}`, since we control the inputs and
 * a leaf-shaped collision shouldn't happen for parameters declared via
 * `propertyPath` constraints.
 */
function setAtPath(
  target: Record<string, unknown>,
  path: readonly string[],
  leafKey: string,
  value: unknown
): void {
  let cursor: Record<string, unknown> = target;
  for (const segment of path) {
    const next = cursor[segment];
    if (typeof next === 'object' && next !== null && !Array.isArray(next)) {
      cursor = next as Record<string, unknown>;
    } else {
      const created: Record<string, unknown> = {};
      cursor[segment] = created;
      cursor = created;
    }
  }
  cursor[leafKey] = value;
}

/**
 * Prepare form values for API submission
 * Removes undefined values and converts types as needed.
 *
 * Parameters that declare `Constraints.PropertyPath` in their UI definition are
 * nested under that path (e.g. `propertyPath: ["authProvider"]` for AAD/MI
 * fields like `Tenant`/`ClientId`/`Secret`/`Type`/`Identity`). Without this,
 * those fields leak flat at parameterValues root and the runtime can't read
 * them under the `authProvider` block via `ServiceProviderEngine
 * .GetAuthProviderSection`. See issue #371.
 */
export function prepareParameterValuesForApi(
  values: FormValues,
  parameterSet: ConnectionParameterSet
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, param] of Object.entries(parameterSet.parameters)) {
    // Skip displayName - it's handled at the properties level, not in parameterValues
    if (key === 'displayName') {
      continue;
    }

    // Omit fields whose dependent-parameter constraint isn't satisfied
    // (e.g. `Secret` when `CredentialType=Certificate`). The runtime treats a
    // present-but-empty value as a real value and rejects the payload.
    if (!isParameterVisible(param as ParameterWithDependency, values)) {
      continue;
    }

    const value = values[key];

    // Skip undefined/null values unless required
    if (value === undefined || value === null) {
      continue;
    }

    // Skip empty strings for optional parameters
    if (value === '' && !isParamRequired(param)) {
      continue;
    }

    // Skip OAuth settings (handled separately)
    if (param.type === PT.OAuthSetting) {
      continue;
    }

    // Skip managed identity (handled separately)
    if (param.type === PT.ManagedIdentity) {
      continue;
    }

    const converted = convertValueForApi(value, param.type);
    const propertyPath = param.uiDefinition?.constraints?.propertyPath;
    if (Array.isArray(propertyPath) && propertyPath.length > 0) {
      setAtPath(result, propertyPath, key, converted);
    } else {
      result[key] = converted;
    }
  }

  return result;
}

/**
 * Check if parameter is required (helper)
 */
function isParamRequired(param: { uiDefinition?: { constraints?: { required?: unknown } } }): boolean {
  const required = param.uiDefinition?.constraints?.required;
  return required === true || required === 'true';
}

/**
 * Convert a value to the appropriate type for API submission
 */
function convertValueForApi(value: unknown, type: string): unknown {
  switch (type) {
    case PT.Bool:
      return value === true || value === 'true';
    case PT.Int:
      return typeof value === 'number' ? value : parseInt(String(value), 10);
    case PT.SecureString:
    case PT.String:
    default:
      return String(value);
  }
}

/**
 * Extract connection name from form values
 * Looks for common connection name field patterns
 */
export function getConnectionNameFromValues(values: FormValues): string | undefined {
  const nameFields = ['connectionName', 'name', 'displayName'];
  for (const field of nameFields) {
    const value = values[field];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

// =============================================================================
// UNIQUE NAME GENERATION
// =============================================================================

/**
 * Generates a unique name by checking against existing keys.
 * Appends -1, -2, -3, etc. until a unique name is found.
 *
 * @param keys - Array of existing connection names
 * @param prefix - The base name to use (usually the connector name)
 * @returns Object containing the unique name and the index used
 */
export function getUniqueName(keys: string[], prefix: string): { name: string; index: number } {
  // Normalize keys to lowercase for case-insensitive comparison
  // Split on '::' to handle namespaced keys (e.g., "outlook::subscription1")
  const set = new Set(keys.map((name) => (name.split('::')[0] ?? '').toLowerCase()));

  let index = 0;
  let name = prefix;

  // Keep incrementing until we find an unused name
  while (set.has(name.toLowerCase())) {
    name = `${prefix}-${++index}`;
  }

  return { name, index };
}

/**
 * Checks if a resource ID is an ARM resource ID.
 * ARM IDs start with /subscriptions/ or similar patterns.
 *
 * @param resourceId - The resource ID to check
 * @returns true if ARM resource, false otherwise
 */
export function isArmResourceId(resourceId: string): boolean {
  return resourceId?.toLowerCase().startsWith('/subscriptions/');
}

/**
 * Generate a suggested connection name from connector info
 * @deprecated Use getUniqueName for better uniqueness handling
 */
export function generateConnectionName(
  connectorName: string,
  existingNames: string[] = []
): string {
  // Clean the connector name - keep alphanumeric and hyphens
  const baseName = connectorName
    .replace(/[^a-zA-Z0-9-]/g, '')
    .substring(0, 20);

  // Use the new getUniqueName function
  const { name } = getUniqueName(existingNames, baseName);
  return name;
}

/**
 * Validate connection name format
 */
export function validateConnectionName(name: string): { valid: boolean; error?: string } {
  if (!name || !name.trim()) {
    return { valid: false, error: 'Connection name is required' };
  }

  if (name.length < 2) {
    return { valid: false, error: 'Connection name must be at least 2 characters' };
  }

  if (name.length > 80) {
    return { valid: false, error: 'Connection name must be 80 characters or less' };
  }

  // Azure resource naming rules
  const validPattern = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
  if (!validPattern.test(name)) {
    return {
      valid: false,
      error: 'Connection name must start with a letter and contain only letters, numbers, underscores, and hyphens',
    };
  }

  return { valid: true };
}

/**
 * Get initial form values for a parameter set
 * Re-exported from connectionParameters for convenience
 */
export { getInitialFormValues } from '@/types/connectionParameters';

// =============================================================================
// PARAMETER DEPENDENCY FUNCTIONS
// =============================================================================

/**
 * Dependent parameter definition
 */
export interface DependentParameter {
  /** The parameter key that this depends on */
  parameter: string;
  /** The value the parameter must have for this to be visible */
  value: unknown;
}

/**
 * Parameter with dependencies and visibility rules
 */
export interface ParameterWithDependency {
  type: string;
  uiDefinition?: {
    displayName?: string;
    description?: string;
    tooltip?: string;
    constraints?: {
      required?: unknown;
      hidden?: string | boolean;
      dependentParameter?: DependentParameter;
      capability?: string[];
    };
  };
  visibility?: string;
}

/**
 * Check if a parameter should be visible based on its dependencies
 *
 * @param parameter - The parameter definition
 * @param currentValues - Current form values
 * @returns Whether the parameter should be visible
 */
export function isParameterVisible(
  parameter: ParameterWithDependency,
  currentValues: FormValues
): boolean {
  // Check explicit hidden constraint
  const constraints = parameter.uiDefinition?.constraints;
  if (constraints?.hidden === true || constraints?.hidden === 'true') {
    return false;
  }

  // Check dependent parameter constraint
  const dependency = constraints?.dependentParameter;
  if (dependency?.parameter) {
    const currentValue = currentValues[dependency.parameter];
    // If the dependency value doesn't match, hide this parameter
    if (currentValue !== dependency.value) {
      return false;
    }
  }

  // Check visibility setting
  if (parameter.visibility === 'internal') {
    return false;
  }

  return true;
}

/**
 * Get visible parameters from a parameter set based on current values
 *
 * @param parameterSet - The parameter set
 * @param currentValues - Current form values
 * @returns Filtered parameters that should be visible
 */
export function getVisibleParameters(
  parameterSet: ConnectionParameterSet,
  currentValues: FormValues
): Record<string, ConnectionParameterSet['parameters'][string]> {
  const result: Record<string, ConnectionParameterSet['parameters'][string]> = {};

  for (const [key, param] of Object.entries(parameterSet.parameters)) {
    if (isParameterVisible(param as ParameterWithDependency, currentValues)) {
      result[key] = param;
    }
  }

  return result;
}

/**
 * Get all parameter dependencies from a parameter set
 *
 * @param parameterSet - The parameter set
 * @returns Map of parameter key to its dependencies
 */
export function getParameterDependencies(
  parameterSet: ConnectionParameterSet
): Map<string, DependentParameter> {
  const dependencies = new Map<string, DependentParameter>();

  for (const [key, param] of Object.entries(parameterSet.parameters)) {
    const dependency = (param as ParameterWithDependency).uiDefinition?.constraints?.dependentParameter;
    if (dependency?.parameter) {
      dependencies.set(key, dependency);
    }
  }

  return dependencies;
}

/**
 * Check if any parameters have capability constraints (gateway vs cloud)
 *
 * @param parameterSet - The parameter set
 * @returns Whether there are capability-filtered parameters
 */
export function hasCapabilityConstraints(
  parameterSet: ConnectionParameterSet
): boolean {
  return Object.values(parameterSet.parameters).some(
    (param) => (param as ParameterWithDependency).uiDefinition?.constraints?.capability?.length
  );
}

/**
 * Filter parameters by capability (cloud, gateway, etc.)
 *
 * @param parameterSet - The parameter set
 * @param capability - The capability to filter by (e.g., 'cloud', 'gateway')
 * @returns Filtered parameters for the given capability
 */
export function filterParametersByCapability(
  parameterSet: ConnectionParameterSet,
  capability: string
): Record<string, ConnectionParameterSet['parameters'][string]> {
  const result: Record<string, ConnectionParameterSet['parameters'][string]> = {};

  for (const [key, param] of Object.entries(parameterSet.parameters)) {
    const capabilities = (param as ParameterWithDependency).uiDefinition?.constraints?.capability;

    // If no capability constraint, include in all
    if (!capabilities?.length) {
      result[key] = param;
      continue;
    }

    // Check if the parameter matches the requested capability
    if (capabilities.includes(capability)) {
      result[key] = param;
    }
  }

  return result;
}

/**
 * Get required parameters from a parameter set
 */
export function getRequiredParameters(
  parameterSet: ConnectionParameterSet
): string[] {
  return Object.entries(parameterSet.parameters)
    .filter(([, param]) => isParamRequired(param as { uiDefinition?: { constraints?: { required?: unknown } } }))
    .map(([key]) => key);
}

/**
 * Check if all required parameters have values
 */
export function allRequiredParametersFilled(
  parameterSet: ConnectionParameterSet,
  values: FormValues
): boolean {
  const required = getRequiredParameters(parameterSet);
  return required.every((key) => {
    const value = values[key];
    return value !== undefined && value !== null && value !== '';
  });
}
